import { useCallback, useState } from 'react';
import { logger } from '@/lib/logging';

/** Per-viewer convenience (Q50): which help videos this browser has played to the end. */
export const WATCHED_HELP_STORAGE_KEY = 'harry.help.watched';

function readWatched(): Set<string> {
  try {
    const raw = window.localStorage.getItem(WATCHED_HELP_STORAGE_KEY);
    const ids: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(ids) ? ids.filter((id): id is string => typeof id === 'string') : []);
  } catch (error) {
    // Private windows and blocked storage: Help works without the ticks.
    logger.warn('Help: could not read watched videos', { error });
    return new Set();
  }
}

export interface WatchedHelp {
  watched: ReadonlySet<string>;
  markWatched: (id: string) => void;
}

export function useWatchedHelp(): WatchedHelp {
  const [watched, setWatched] = useState<ReadonlySet<string>>(readWatched);
  const markWatched = useCallback((id: string) => {
    setWatched((current) => {
      if (current.has(id)) return current;
      const next = new Set(current).add(id);
      try {
        window.localStorage.setItem(WATCHED_HELP_STORAGE_KEY, JSON.stringify([...next]));
      } catch (error) {
        logger.warn('Help: could not save watched videos', { error });
      }
      return next;
    });
  }, []);
  return { watched, markWatched };
}
